import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { DrawdownBatchService } from '@/services/drawdowns/drawdownBatchService';
import type {
  DrawdownBatch,
  DrawdownBatchUpdateResponse,
  DrawdownRecalculationRequest,
  DrawdownRecalculationResponse,
} from '@/types/drawdown';

/**
 * React Query hooks for drawdown batch operations
 *
 * Handles batch CRUD, PDF generation, email sending, and recalculation
 */

// ==================== Query Hooks ====================

/**
 * Get all batches for a specific drawdown
 */
export const useDrawdownBatches = (fundId: string, drawdownId: string) => {
  return useApiQuery(
    [...queryKeys.drawdowns.detail(drawdownId), 'batches'],
    () => DrawdownBatchService.getBatches(fundId, drawdownId),
    {
      enabled: !!fundId && !!drawdownId,
      staleTime: 30 * 1000, // 30 seconds
    }
  );
};

/**
 * Get batch preview with investor notices
 */
export const useBatchPreview = (
  fundId: string,
  drawdownId: string,
  batchId: string
) => {
  return useApiQuery(
    [...queryKeys.drawdowns.detail(drawdownId), 'batches', batchId, 'preview'],
    () => DrawdownBatchService.getBatchPreview(fundId, drawdownId, batchId),
    {
      enabled: !!fundId && !!drawdownId && !!batchId,
      staleTime: 30 * 1000, // 30 seconds
    }
  );
};

// ==================== Mutation Hooks ====================

/**
 * Generate PDFs for a batch
 */
export const useGenerateBatchPDFs = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    { message: string; batch_id: string; drawdown_id: string },
    Error,
    {
      fundId: string;
      drawdownId: string;
      batchId: string;
      regenerate?: boolean;
    }
  >(
    ({ fundId, drawdownId, batchId, regenerate = false }) =>
      DrawdownBatchService.generateBatchPDFs(
        fundId,
        drawdownId,
        batchId,
        regenerate
      ),
    {
      onSuccess: (_, variables) => {
        // Invalidate batch to refresh PDF status
        queryClient.invalidateQueries({
          queryKey: [
            ...queryKeys.drawdowns.detail(variables.drawdownId),
            'batches',
            variables.batchId,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...queryKeys.drawdowns.detail(variables.drawdownId),
            'batches',
          ],
        });
      },
    }
  );
};

/**
 * Issue a single DRAFT batch
 *
 * For batches added after the drawdown was already issued. Queues the notice
 * PDFs server-side; the drawdown status is left untouched.
 */
export const useIssueBatch = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    DrawdownBatchUpdateResponse,
    Error,
    { fundId: string; drawdownId: string; batchId: string }
  >(
    ({ fundId, drawdownId, batchId }) =>
      DrawdownBatchService.issueBatch(fundId, drawdownId, batchId),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            ...queryKeys.drawdowns.detail(variables.drawdownId),
            'batches',
          ],
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.drawdowns.detail(variables.drawdownId),
        });
      },
    }
  );
};

/**
 * Download every notice PDF in a batch as one ZIP
 */
export const useDownloadBatchPDFs = () =>
  useApiMutation<
    void,
    Error,
    { fundId: string; drawdownId: string; batchId: string }
  >(
    ({ fundId, drawdownId, batchId }) =>
      DrawdownBatchService.downloadBatchPdfsZip(fundId, drawdownId, batchId),
    {
      showSuccessToast: false, // the browser download is the feedback
    }
  );

/**
 * Send emails for a batch
 */
export const useSendBatchEmails = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    {
      message: string;
      batch_id: string;
      drawdown_id: string;
      new_only: boolean;
    },
    Error,
    { fundId: string; drawdownId: string; batchId: string; newOnly?: boolean }
  >(
    ({ fundId, drawdownId, batchId, newOnly = false }) =>
      DrawdownBatchService.sendBatchEmails(
        fundId,
        drawdownId,
        batchId,
        newOnly
      ),
    {
      onSuccess: (_, variables) => {
        // Invalidate batch to refresh email status
        queryClient.invalidateQueries({
          queryKey: [
            ...queryKeys.drawdowns.detail(variables.drawdownId),
            'batches',
            variables.batchId,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...queryKeys.drawdowns.detail(variables.drawdownId),
            'batches',
          ],
        });
        // Update batch status after sending emails
        queryClient.invalidateQueries({
          queryKey: queryKeys.drawdowns.detail(variables.drawdownId),
        });
      },
    }
  );
};

/**
 * Recalculate a drawdown (add investors, create batches)
 */
export const useRecalculateDrawdown = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    DrawdownRecalculationResponse,
    Error,
    { fundId: string; drawdownId: string; data: DrawdownRecalculationRequest }
  >(
    ({ fundId, drawdownId, data }) =>
      DrawdownBatchService.recalculateDrawdown(fundId, drawdownId, data),
    {
      onSuccess: (_, variables) => {
        // Invalidate all batches for this drawdown
        queryClient.invalidateQueries({
          queryKey: [
            ...queryKeys.drawdowns.detail(variables.drawdownId),
            'batches',
          ],
        });
        // Invalidate drawdown detail to refresh investor counts
        queryClient.invalidateQueries({
          queryKey: queryKeys.drawdowns.detail(variables.drawdownId),
        });
        // Invalidate drawdowns list to refresh totals
        queryClient.invalidateQueries({
          queryKey: queryKeys.drawdowns.all,
        });
        // The linked fund transaction carries the same total
        queryClient.invalidateQueries({
          queryKey: queryKeys.fundTransactions.all,
        });
      },
    }
  );
};

// Documents are generated by a server-side queue in slices, so between runs a
// batch can report 'partial'; count-based math is the reliable activity signal
export const batchHasPdfActivity = (b: DrawdownBatch) =>
  b.pdfs_total > 0 && b.pdfs_completed + b.pdfs_failed < b.pdfs_total;

export const hasPdfActivity = (batches?: DrawdownBatch[]) =>
  batches?.some(batchHasPdfActivity) ?? false;

/**
 * Poll batch PDF generation status with configurable interval
 *
 * Returns batch data with PDF generation status for polling.
 * Useful for tracking PDF regeneration progress.
 *
 * @example
 * // Poll every 3 seconds while PDFs are generating
 * const { data: batches } = useBatchesPDFStatus(fundId, drawdownId, {
 *   enabled: true,
 *   refetchInterval: 3000,
 * });
 *
 * // Check if any batch is still generating
 * const anyGenerating = batches?.some(b => b.pdf_generation_status === 'generating');
 */
export const useBatchesPDFStatus = (
  fundId: string,
  drawdownId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data: DrawdownBatch[] | undefined };
        }) => number | false);
  }
) => {
  const { enabled = true, refetchInterval = false } = options || {};

  return useApiQuery<DrawdownBatch[]>(
    [...queryKeys.drawdowns.detail(drawdownId), 'batches', 'pdf-status'],
    () => DrawdownBatchService.getBatches(fundId, drawdownId),
    {
      enabled: !!fundId && !!drawdownId && enabled,
      staleTime: 3 * 1000, // 3 seconds - PDF status changes during regeneration
      refetchInterval,
      refetchIntervalInBackground: false,
    }
  );
};
